<template>
    <div>
        <h2>Differential Diagnosis:</h2>
        <hr>
        <quill-editor 
            @blur="saveSlide()"
            v-model="slide.slide.corpus"
            :options="{'placeholder': 'Write prompt here...'}"
        ></quill-editor>
        <ResourceGallery :resourceList="slide.slide.resources"></ResourceGallery>
        <hr>
        <div style="margin: auto; display: inline-block; padding: 20px;">
            <h3 style="margin-bottom: 10px">Instructions:</h3>
            <span style="font-size: 110%; text-align: justify; float: left;">
                - Drag each diagnosis to the category in which it belongs using the handle (horizontal lines) in the top left.
                <span v-if="!slide.freeResponse">
                    <br>
                    - Learner's scores are calculated based on correctly categorizing each diagnosis by likelihood.
                    <br>
                    - Place the top diagnosis first under "High Likelihood". Sorting is helpful, but otherwise optional.
                </span>
                <br>
                - Write an explanation for how you categorized each diagnosis. It's helpful to include pros/cons.
                <br>
            </span>
        </div>
        <br>
        <hr>
        <center>
            <md-switch v-model="slide.freeResponse" @change="saveSlide()" class="free-response-switch">
                Free Response Mode
            </md-switch>
            <br>
            <i v-if="slide.freeResponse">
                Learners will only see your diagnosis list for feedback. No scoring.
            </i>
            <i v-else>
                Learners will sort the diagnosis you provide and can add their own.
            </i>
        </center>
        <hr>
        <md-button class="md-raised md-primary" @click="vertDx=!vertDx" style="transform: scale(.92)">
            Switch to {{vertDx ? 'Horizontal' : 'Vertical'}} mode
        </md-button>
        <hr>
        <div :class="{'ddx-editor': true, 'ddx-editor-vert': vertDx}">
            <div class="ddx-col" 
            v-for="priorityElem in priorityGroups" 
            :style="{'background-color': lookupPriorityBgColor(priorityElem)}"
            :key="slide.id + '-' + priorityElem">
                <h3>{{priorityElem}}</h3>
                <hr>
                <draggable 
                v-model="dxGroups[priorityElem]" 
                class="dragArea" 
                @end="checkMove(false); checkMove(true);"
                :options="{group:'ddx', handle :'.drag-handle'}">
                    <transition-group name="ddx-entry-move">
                        <div 
                        class="ddx-entry md-elevation-6"
                        v-for="(dxOption, dxIndex) in dxGroups[priorityElem]" 
                        :style="{'background-color': lookupPriorityColor(priorityElem, dxOption)}"
                        :key="dxOption.id + ''">
                            <span class="drag-handle">
                                <md-icon>drag_handler</md-icon>
                            </span>
                            <div style="min-width: 90%; margin-left: -10px;"> 
                                <div v-show="dxOption.order == 1 && priorityElem == 'High Likelihood'" style="font-weight: bold; margin-bottom: 6px">
                                    TOP DIAGNOSIS
                                </div>
                                <div class="dx-order-input">
                                    <md-field class="order-input">
                                        <span class="md-prefix">#</span>
                                        <md-input v-model="dxOption.order" type="number" min=1 :max="dxGroups[priorityElem].length"
                                        @change="rearrangeDiagnosis(priorityElem, dxIndex)" style="text-align: center"/>
                                    </md-field>
                                    <select v-model="dxOption.priority" @change="checkMove(true)" class="priority-select"
                                    :style="{'background-color': lookupPriorityBgColor(priorityElem, dxOption)}">
                                        <option 
                                        v-for="priorityElem in priorityGroups" 
                                        :value="priorityElem"
                                        :key="priorityElem">
                                            {{priorityElem}}
                                        </option>
                                    </select>
                                </div>
                                <div class="dx-entry-input">
                                    <md-field>
                                        <editable
                                            :content="dxOption.dx"
                                            @update="dxOption.dx = $event; editableUpdate()" 
                                            class="div-textarea"
                                            style="width: 100%" 
                                            :placeholder="'Diagnosis #' + (dxIndex+1)">
                                        </editable>
                                    </md-field>
                                    <md-field>
                                        <editable
                                            :content="dxOption.explanation"
                                            @update="dxOption.explanation = $event; editableUpdate()"
                                            class="div-textarea"
                                            style="width: 100%" 
                                            :placeholder="'Explanation #' + (dxIndex+1)">
                                        </editable>
                                    </md-field>
                                </div>
                            </div>
                            <md-button 
                            @click="deleteDDx(priorityElem, dxIndex)" 
                            class="delete-btn md-icon-button">
                                <md-icon class="md-accent">
                                    cancel
                                </md-icon>
                            </md-button>
                        </div>
                    </transition-group>
                    <div slot="footer">
                        <md-button @click="addDDxOpt(priorityElem)" class="md-raised md-primary">
                            Add Diagnosis
                        </md-button>
                    </div>
                </draggable>
            </div>
        </div>
        <hr>
        <h2>Explanation:</h2>
        <quill-editor 
            @blur="saveSlide()"
            v-model="slide.answer"
            :options="{'placeholder': 'Write any further explanation here...'}"
        ></quill-editor>
        <CitationManager :citations="slide.slide.citations" @save="saveSlide(false)" :notRequired="true"/>
        <ResourceGallery :resourceList="slide.a_resources" v-on:saveSlide="saveSlide(false)"></ResourceGallery>
        <hr>
        <md-button class="md-raised md-primary" @click="saveSlide(true)" style="background-color: green">
            Save
        </md-button>
        <md-button class="md-raised md-accent md-icon-button slide-delete-btn" @click="deleteSlide()">
            <md-icon>delete_forever</md-icon>
        </md-button>
    </div>
</template>

<script>

import draggable from 'vuedraggable'
import editable from '../Editable.vue'
import ResourceGallery from './Gallery.vue'
import CitationManager from './CitationManager.vue'

const ObjectID = require("bson-objectid");

export default {
    props: {
        slide: {
            type: Object
        },
    },
    data: function(){
        return {
            vertDx: true,
            answer: "",
            priorityChange: false,
            priorityGroups: ["Not Likely", "Low Likelihood", "High Likelihood"],
            dxGroups: {},
            saveTimer: undefined
        }
    },
    components: {
        draggable,
        editable,
        ResourceGallery,
        CitationManager,
    },
    methods: {
        editableUpdate: function(){
            clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => {
                this.saveSlide()
            }, 750)
        },
        lookupPriorityColor: function(priority, dxOption){
            if (priority == "High Likelihood" && dxOption.order == 1) {
                return "#f77890"
            }
            const colorLookup = {
                "Not Likely": "#eee", 
                "Low Likelihood": "#fdff9a", 
                "High Likelihood": "#ffbe93"
                //Green = 74e38d
            }
            if(!Object.keys(colorLookup).includes(priority)){
                return "white"
            }
            return colorLookup[priority]
        },
        lookupPriorityBgColor: function(priority, dxOption){
            if(dxOption && dxOption.order != undefined) {
                if (priority == "High Likelihood" && dxOption.order == 1) {
                    return "#dc6b81"
                }
            }
            const colorLookup = {
                "Not Likely": "#ddd", 
                "Low Likelihood": "#f8fa63", 
                "High Likelihood": "#ffa56a"
                //Green = 4ace68
            }
            if(!Object.keys(colorLookup).includes(priority)){
                return "white"
            }
            return colorLookup[priority]
        },
        saveSlide: function(closeSlide, usePriority) {
            this.unsortDiagnosisData(usePriority)
            this.$emit('saveSlide', [this.slide, closeSlide])
        },
        deleteSlide: function(){
            this.$emit('deleteSlide', this.slide)
        },
        createDDxEntry: function(priority){
            return {
                id: ObjectID(),
                order: this.dxGroups[priority].length+1,
                dx: "",
                priority,
                explanation: ""
            }
        },
        deleteDDx: function(priority, ddxIndex){
            this.dxGroups[priority].splice(ddxIndex, 1)
            this.saveSlide()
        },
        addDDxOpt: function(priority) {
            if(!this.dxGroups[priority]) this.dxGroups[priority] = []
            this.dxGroups[priority].push(this.createDDxEntry(priority))
        },
        rearrangeDiagnosis(priority, oldIndex){
            let rearrange = () => {
                let newIndex = this.dxGroups[priority][oldIndex].order-1
                let newDxGroup = []
                if(newIndex < 0){
                    newIndex = 0 
                } 
                if(newIndex > this.dxGroups[priority].length-1){
                    newIndex = this.dxGroups[priority].length-1
                }
                if(newIndex > oldIndex){
                    this.dxGroups[priority].forEach((ddxElem, ddxIndex) => {
                        if(ddxIndex > oldIndex && ddxIndex <= newIndex){
                            ddxElem.order = ddxIndex
                            newDxGroup[ddxIndex-1] = ddxElem
                        } else if(oldIndex == ddxIndex) {
                            ddxElem.order = newIndex + 1
                            newDxGroup[newIndex] = ddxElem
                        } else {
                            ddxElem.order = ddxIndex + 1
                            newDxGroup[ddxIndex] = ddxElem
                        }
                    })
                } else if (newIndex < oldIndex){
                    this.dxGroups[priority].forEach((ddxElem, ddxIndex) => {
                        if(ddxIndex < oldIndex && ddxIndex >= newIndex){
                            ddxElem.order = ddxIndex+2
                            newDxGroup[ddxIndex+1] = ddxElem
                        } else if(oldIndex == ddxIndex) {
                            ddxElem.order = newIndex + 1
                            newDxGroup[newIndex] = ddxElem
                        } else {
                            ddxElem.order = ddxIndex + 1
                            newDxGroup[ddxIndex] = ddxElem
                        }
                    })
                } else {
                    newDxGroup = this.dxGroups[priority]
                }
                this.$set(this.dxGroups, priority, newDxGroup)
                this.saveSlide(false)
                this.sortDiagnosisData()
            }
            setTimeout(rearrange, 250)
        },
        sortDiagnosisData: function(){
            let renderData = {}
            this.priorityGroups.forEach((priorityElem) => renderData[priorityElem] = [])
            if(this.slide.ddx_opts){
                this.slide.ddx_opts.forEach((ddxOpt) => {
                    ddxOpt.order = renderData[ddxOpt.priority].length + 1                    
                    renderData[ddxOpt.priority].push(ddxOpt)
                })
            }
            this.dxGroups = renderData
        },
        unsortDiagnosisData: function(usePriority){
            let dbData = []
            Array().concat(this.priorityGroups).reverse().forEach((priorityElem) => {
                if(this.dxGroups[priorityElem]){
                    this.dxGroups[priorityElem].forEach((ddxElem) => {
                        if(!ddxElem.priority || !usePriority) ddxElem.priority = priorityElem
                        dbData.push(ddxElem)
                    })
                }
            })
            dbData = dbData.filter((ddxElem) => {
                return ddxElem.dx && ddxElem.dx != ""
            })
            this.$set(this.slide, "ddx_opts", dbData)
        },
        checkMove: function(usePriority){
            this.saveSlide(false, usePriority)
            this.sortDiagnosisData();
        }
    },
    watch: {
        slide: function(){
            this.sortDiagnosisData()
        }
    },
    mounted: function(){
        this.$nextTick(function() {
            this.sortDiagnosisData()
        })
    }
}
</script>

<style scoped>
    h3{
        margin: 5px;
    }
    .md-field{
        text-align: left;
        width: 90% !important;
        margin: auto;
        margin-bottom: 10px;
    }
    .ddx-editor{
        transition: all 600ms;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
    }
    .ddx-editor.ddx-editor-vert{
        flex-direction: row !important;
        width: 100%;
        overflow-x: auto;
        justify-content: flex-start;
        padding-bottom: 15px;
    }
    .vertical-layout .ddx-editor{
        margin: 0 -20px;
        width: calc(100% + 40px);
    }
    .ddx-col{
        border: 1px solid black;
        border-radius: 8px;
        min-width: 90%;
        margin: 10px 0;
        padding: 10px 0px;
    }
    .ddx-editor-vert .ddx-col{
        width: 31%;  
        height: 84vh;  
        padding: 10px 5px;  
        min-width: 340px;  
        margin: 8px 8px;
    }
    @media (max-width: 400px) {
        .ddx-col{
            min-width: 94%;
        }
    }
    .vertical-layout .ddx-col {
        font-size: 90%;
    }
    .priority-select{
        height: 36px;
        text-align-last: center;
    }
    .vertical-layout .priority-select{
        font-size: 100%;
    }
    .order-input{
        max-width: 50px; 
        margin: 0px; 
        margin-right: 20px; 
        margin-top: -12px;
        font-size: 90%;
    }
    .order-input input[type=number] {
        padding-right: 10px;
    }
    .order-input input[type=number]::-webkit-inner-spin-button, 
    .order-input input[type=number]::-webkit-outer-spin-button { 
        -webkit-appearance: none;
    }
    .vertical-layout .order-input{
        margin-right: 10px; 
    }
    .order-input input{
        max-width: 80%;
        -moz-appearance:textfield;
    }
    .vertical-layout .order-input input, .vertical-layout .order-input .md-prefix{
        font-weight: bold;
        font-size: 100%;
    }
    .ddx-col .draggable{
        min-height: 120px;
    }
    .ddx-entry{
        transition: all 600ms;
        margin: 0 15px 25px 15px;
        padding: 8px;
        border-radius: 5px;
        display: flex;
        min-width: 280px;
        max-width: 280px;
        position: relative;
    }
    .ddx-editor-vert .ddx-entry{
        width: 92%; 
        margin: 12px auto;
    }
    .ddx-entry-move-enter, .ddx-entry-move-leave-active {
        opacity: 0;
    }
    .dragArea{
        display: flex;
        flex-direction: column;
        width: 100%;
    }
    .ddx-editor-vert .dragArea{
        height: 86%;  
    }
    .dragArea > span{
        overflow-x: scroll;
        display: flex;
        min-height: 120px;
    }
    .ddx-editor-vert .dragArea > span{
        overflow-x: hidden;  
        overflow-y: scroll;  
        display: block;
        min-height: 86%; 
    }
    .drag-handle{
        cursor: move;
        padding: 1px 0 20px 0;
    }
    .vertical-layout .drag-handle {
        display: none;
    }
    .md-textarea{
        overflow-y: hidden !important;
        margin: 4px;
    }
    [contenteditable=true]:empty:before {
        content: attr(placeholder);
        color: #868686;
        display: block; /* For Firefox */
    }
    .div-textarea{
        cursor: text;
        line-height: 1.15;  
        padding: 5px 7px;
        max-height: 100px; 
        overflow-y: auto; 
    }
    .delete-btn{
        position: absolute;
        top: 5px;
        right: 0px;
    }
    .vertical-layout .free-response-switch{
        margin-bottom: 34px;
    }
    .dx-order-input {
        display: flex; 
        align-items: center; 
        justify-content: center;
        width: 90%;
    }
    .dx-entry-input{
        max-width: 300px;
    }
</style>